import type { APIDesignerStore } from '..';
import type { CODE_SAMPLES, Extensions } from 'oas/extensions';
import type { OpenAPIV3 } from 'openapi-types';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

import { MIMEType } from '../BodySchemaEditor/utils';

/**
 * NOTE: Code Samples vs Request Body Examples
 *
 * This slice manages two distinct types of examples for API operations:
 *
 * 1. Code Samples (shell, Node.js, Python, etc.)
 *    - Stored in OpenAPI extensions: `x-code-samples` or `x-readme.code-samples`
 *    - Example structure:
 *    ```json
 *    {
 *      "x-code-samples": [
 *        {
 *          "lang": "shell",
 *          "source": "curl -X POST https://api.example.com/users",
 *          "name": "Create User"
 *        }
 *      ]
 *    }
 *    ```
 *
 * 2. Request Body Examples (JSON payload examples)
 *    - Stored in OpenAPI `requestBody.content['application/json'].examples`
 *    - Example structure:
 *    ```json
 *    {
 *      "requestBody": {
 *        "content": {
 *          "application/json": {
 *            "examples": {
 *              "User Example": {
 *                "summary": "User Example",
 *                "value": {
 *                  "name": "John Doe",
 *                  "email": "john@example.com"
 *                }
 *              }
 *            }
 *          }
 *        }
 *      }
 *    }
 *    ```
 *
 * Both are stored in the same slice because they're both examples for the current operation,
 * but they serve different purposes and are stored in different parts of the OpenAPI spec.'
 *
 * Also note that we currently only support JSON payload examples.
 */

export interface RequestExampleEditorSliceState {
  /**
   * The custom code samples for the current operation (shell, Node.js, Python, etc.)
   */
  codeSamples: NonNullable<Extensions[typeof CODE_SAMPLES]>;

  /**
   * Determines if the code samples editor is in the initial state with no examples
   * or a custom state where the user is writing custom examples.
   */
  editorState: 'custom' | 'initial';

  /**
   * Indicates whether the given operation has custom code samples or not.
   */
  hasExamples: boolean;

  /**
   * The index of the currently selected code sample.
   */
  selectedExample: number;
}

export interface RequestExampleEditorSliceActions {
  /**
   * Adds a custom code sample to the current operation (shell, Node.js, Python, etc.).
   */
  addCodeSample: () => void;

  /**
   * Adds a request body example to the current operation.
   * Will add an example to the specified media type.
   * If the request body or media type does not exist, it will be created.
   * Returns the index of the newly created example in the full list across all media types.
   */
  addRequestBodyExample: (mediaType: string) => number;

  /**
   * Changes the media type of a request body example.
   */
  changeRequestBodyExampleMediaType: ({
    oldMediaType,
    newMediaType,
    exampleName,
  }: {
    oldMediaType: string;
    newMediaType: string;
    exampleName: string;
  }) => void;

  /**
   * Deletes the currently selected code sample (shell, Node.js, Python, etc.).
   */
  deleteCodeSample: () => void;

  /**
   * Deletes a request body example by name and media type.
   */
  deleteRequestBodyExample: (exampleName: string, mediaType: string) => void;

  /**
   * Gets the request body examples for the current operation directly from the API object.
   * Returns an array of example objects with mediaType, summary, and value fields.
   * Examples are sorted with application/json first, then alphabetically by media type.
   */
  getRequestBodyExamples: () => { mediaType: string; summary: string; value: unknown }[];

  initialize: () => void;

  /**
   * Updates the language of the currently selected code sample.
   */
  setCodeSampleLanguage: (language: string) => void;

  /**
   * Sets the code samples editor state to the provided value.
   */
  setEditorState: (editorState: RequestExampleEditorSliceState['editorState']) => void;

  /**
   * Updates the index of the currently selected code sample.
   */
  setSelectedCodeSample: (exampleNumber: number) => void;

  /**
   * Updates the code of the currently selected code sample.
   */
  updateCodeSampleCode: (editor, data, value: string) => void;

  /**
   * Updates the name of the currently selected code sample.
   */
  updateCodeSampleName: (exampleName: string) => void;

  /**
   * Updates the name of a request body example.
   */
  updateRequestBodyExampleName: (newName: string, oldName: string, mediaType: string) => void;

  /**
   * Updates the value of a request body example.
   */
  updateRequestBodyExampleValue: (value: string, exampleName: string, mediaType: string) => void;

  /**
   * Updates the `samples-languages` array for the current operation.
   * It rebuilds the array from the code samples in the operation.
   * For parity with the old editor (and for the UX, to be clear) we're making this match
   * the defined code samples in the Designer.
   *
   * @see {@link https://docs.readme.com/main/docs/openapi-extensions#code-sample-languages}
   */
  updateSamplesLanguages: () => void;
}

export interface RequestExampleEditorSlice {
  /**
   * State slice containing fields and actions that pertain to the Request Example Editor
   */
  requestExampleEditor: RequestExampleEditorSliceActions & RequestExampleEditorSliceState;
}

export const initialState: RequestExampleEditorSliceState = {
  codeSamples: [],
  editorState: 'initial',
  hasExamples: false,
  selectedExample: 0,
};

/**
 * Request Example Editor state slice containing fields related to the Manual Editor — Request Example Editor.
 */
export const createRequestExampleEditorSlice: StateCreator<
  APIDesignerStore & RequestExampleEditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  RequestExampleEditorSlice
> = (set, get) => ({
  requestExampleEditor: {
    ...initialState,

    /**
     * //////////////////////////////////
     * Code Samples Functions
     * //////////////////////////////////
     */

    addCodeSample: () => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newSnippet = {
        code: '',
        language: 'shell',
        name: '',
      };

      const newOperation = structuredClone(operation);

      if (!get().requestExampleEditor.hasExamples) {
        // If we get to this point, an OAS and operation should exist, so we can safely
        // assume that we can find an operation for the given path and method.
        newOperation!['x-readme'] = {
          'code-samples': [newSnippet],
        };
      } else if (get().requestExampleEditor.hasExamples) {
        const xCodeSamples = newOperation?.['x-code-samples'];
        if (xCodeSamples) {
          xCodeSamples.push(newSnippet);
        }

        if (newOperation!['x-readme']?.['code-samples']) {
          newOperation!['x-readme']['code-samples'].push(newSnippet);
        }
      }

      const codeSamples = newOperation?.['x-readme']?.['code-samples'] || operation?.['x-code-samples'];
      const hasExamples = !!codeSamples?.length;

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.requestExampleEditor.codeSamples = codeSamples || [];
          state.requestExampleEditor.editorState = 'custom';
          state.requestExampleEditor.hasExamples = hasExamples;
          state.requestExampleEditor.selectedExample = codeSamples ? codeSamples!.length - 1 : 0;
        },
        false,
        actionLog('adding request example', { newOperation }),
      );

      get().requestExampleEditor.updateSamplesLanguages();
    },

    deleteCodeSample() {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      const xCodeSamples = newOperation?.['x-code-samples'];
      if (xCodeSamples) {
        if (xCodeSamples.length === 1) {
          delete newOperation!['x-code-samples'];
          get().requestExampleEditor.setEditorState('initial');
        } else {
          xCodeSamples.splice(get().requestExampleEditor.selectedExample, 1);
        }
      }

      if (newOperation!['x-readme']?.['code-samples']) {
        if (newOperation!['x-readme']['code-samples'].length === 1) {
          delete newOperation!['x-readme']['code-samples'];
          get().requestExampleEditor.setEditorState('initial');
        } else {
          newOperation!['x-readme']['code-samples'].splice(get().requestExampleEditor.selectedExample, 1);
        }
      }

      // Ensure we update the selected example if we're deleting the last example in the list
      if (
        get().requestExampleEditor.selectedExample === get().requestExampleEditor.codeSamples.length - 1 &&
        get().requestExampleEditor.codeSamples.length > 1
      ) {
        get().requestExampleEditor.setSelectedCodeSample(get().requestExampleEditor.selectedExample - 1);
      }

      const codeSamples = newOperation?.['x-readme']?.['code-samples'] || operation?.['x-code-samples'];
      const hasExamples = !!codeSamples?.length;

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.requestExampleEditor.codeSamples = codeSamples || [];
          state.requestExampleEditor.hasExamples = hasExamples;
        },
        false,
        actionLog('deleting request example', { newOperation }),
      );

      get().requestExampleEditor.updateSamplesLanguages();
    },

    initialize: () => {
      const { operation } = get().getCurrentOperation();

      const codeSamples = operation?.['x-readme']?.['code-samples'] || operation?.['x-code-samples'];
      const hasExamples = !!codeSamples?.length;

      set(
        state => {
          state.requestExampleEditor.codeSamples = codeSamples || [];
          state.requestExampleEditor.editorState = hasExamples ? 'custom' : 'initial';
          state.requestExampleEditor.hasExamples = hasExamples;
        },
        false,
        actionLog('initialize request example editor slice', {}),
      );
    },

    setEditorState(editorState: RequestExampleEditorSliceState['editorState']) {
      set(
        state => {
          state.requestExampleEditor.editorState = editorState;
        },
        false,
        actionLog('setting request editor state', { editorState }),
      );
    },

    setCodeSampleLanguage(language: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      if (newOperation!['x-code-samples']) {
        newOperation!['x-code-samples'][get().requestExampleEditor.selectedExample].language = language;
      }

      if (newOperation!['x-readme']?.['code-samples']) {
        newOperation!['x-readme']['code-samples'][get().requestExampleEditor.selectedExample].language = language;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          const codeSamples = newOperation?.['x-readme']?.['code-samples'] || operation?.['x-code-samples'];
          state.requestExampleEditor.codeSamples = codeSamples || [];
        },
        false,
        actionLog('setCodeSampleLanguage', { language }),
      );

      get().requestExampleEditor.updateSamplesLanguages();
    },

    setSelectedCodeSample(exampleNumber: number) {
      set(
        state => {
          state.requestExampleEditor.selectedExample = exampleNumber;
        },
        false,
        actionLog('setting selected request example', { exampleNumber }),
      );
    },

    updateCodeSampleName(exampleName: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      if (newOperation!['x-code-samples']) {
        newOperation!['x-code-samples'][get().requestExampleEditor.selectedExample].name = exampleName;
      }

      if (newOperation!['x-readme']?.['code-samples']) {
        newOperation!['x-readme']['code-samples'][get().requestExampleEditor.selectedExample].name = exampleName;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('updating code sample name', { exampleName }),
      );
    },

    updateSamplesLanguages() {
      const generateSamplesLanguages = (codeSamples: NonNullable<Extensions[typeof CODE_SAMPLES]>) => {
        return Array.from(new Set(codeSamples.map(sample => sample.language)));
      };

      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      if (!operation) {
        throw new Error('Operation not found');
      }

      const newOperation = structuredClone(operation);

      // We support both the legacy `x-code-samples` location and the newer `x-readme.code-samples`.
      let samplesLanguages: string[] = [];

      if (newOperation['x-code-samples']) {
        if (!Array.isArray(newOperation['x-code-samples'])) {
          return;
        }
        samplesLanguages = generateSamplesLanguages(newOperation['x-code-samples']);
      } else if (newOperation['x-readme']?.['code-samples']) {
        samplesLanguages = generateSamplesLanguages(newOperation['x-readme']['code-samples']);
      }

      if (samplesLanguages.length > 0) {
        // Keep `x-readme.samples-languages` in sync with the actual languages present.
        if (!newOperation['x-readme']) {
          newOperation['x-readme'] = {} as Extensions;
        }
        (newOperation['x-readme'] as Extensions)['samples-languages'] = samplesLanguages;
      } else if (newOperation['x-readme']?.['samples-languages']) {
        // No samples remain: remove `samples-languages` to avoid leaving stale/empty arrays.
        // Clone and delete to keep TS happy about the delete operator usage.
        const xreadme = { ...(newOperation['x-readme'] as Record<string, unknown>) };
        delete xreadme['samples-languages'];
        newOperation['x-readme'] = xreadme as unknown as Extensions;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('updating samples languages'),
      );
    },

    updateCodeSampleCode(_editor, _data, value: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      if (newOperation!['x-code-samples']) {
        newOperation!['x-code-samples'][get().requestExampleEditor.selectedExample].code = value;
      }

      if (newOperation!['x-readme']?.['code-samples']) {
        newOperation!['x-readme']['code-samples'][get().requestExampleEditor.selectedExample].code = value;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('updating code sample code', { value }),
      );
    },

    /**
     * //////////////////////////////////
     * Request Body Examples Functions
     * /////////////////////////////////
     */

    addRequestBodyExample(mediaType: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      if (!newOperation?.requestBody) {
        newOperation!.requestBody = {
          content: {
            [mediaType]: {
              schema: { type: 'object' },
              examples: {},
            },
          },
        } as OpenAPIV3.RequestBodyObject;
      }

      const requestBody = newOperation!.requestBody as OpenAPIV3.RequestBodyObject;

      if (!requestBody.content) {
        requestBody.content = {
          [mediaType]: {
            schema: { type: 'object' },
            examples: {},
          },
        };
      }

      if (!requestBody.content[mediaType]) {
        requestBody.content[mediaType] = {
          schema: { type: 'object' },
          examples: {},
        };
      }

      if (!requestBody.content[mediaType].examples) {
        requestBody.content[mediaType].examples = {};
      }

      const baseExampleLabel = 'New Example';
      let newExampleCount = 1;
      let newExampleLabel = baseExampleLabel;
      const currentExampleLabels = Object.keys(requestBody.content[mediaType].examples);

      while (currentExampleLabels.includes(newExampleLabel)) {
        newExampleLabel = `${baseExampleLabel} ${newExampleCount}`;
        newExampleCount += 1;
      }

      const newExampleValue = {
        summary: newExampleLabel,
        value: {},
      };

      requestBody.content[mediaType].examples![newExampleLabel] = newExampleValue;

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('adding request body example', { newOperation }),
      );

      const allExamples = get().requestExampleEditor.getRequestBodyExamples();
      const newExampleIndex = allExamples.findIndex(ex => ex.mediaType === mediaType && ex.summary === newExampleLabel);

      return newExampleIndex;
    },

    changeRequestBodyExampleMediaType({
      oldMediaType,
      newMediaType,
      exampleName,
    }: {
      oldMediaType: string;
      newMediaType: string;
      exampleName: string;
    }) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const requestBody = operation?.requestBody as OpenAPIV3.RequestBodyObject | undefined;
      if (!requestBody?.content?.[oldMediaType]?.examples) {
        return;
      }

      const newOperation = structuredClone(operation);
      const newRequestBody = newOperation!.requestBody as OpenAPIV3.RequestBodyObject;

      // Get the example from the old media type
      const oldExamples = newRequestBody.content![oldMediaType]!.examples!;
      const exampleToMove = oldExamples[exampleName];

      if (!exampleToMove) {
        return;
      }

      // Remove from old media type
      delete oldExamples[exampleName];

      // Add to new media type
      if (!newRequestBody.content![newMediaType]) {
        newRequestBody.content![newMediaType] = {
          schema: { type: 'object' },
          examples: {},
        };
      }

      if (!newRequestBody.content![newMediaType]!.examples) {
        newRequestBody.content![newMediaType]!.examples = {};
      }

      newRequestBody.content![newMediaType]!.examples![exampleName] = exampleToMove;

      // Clean up empty examples objects
      if (Object.keys(oldExamples).length === 0) {
        delete newRequestBody.content![oldMediaType]!.examples;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('changing request body example media type', { oldMediaType, newMediaType, exampleName }),
      );
    },

    deleteRequestBodyExample(exampleName: string, mediaType: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const requestBody = operation?.requestBody as OpenAPIV3.RequestBodyObject | undefined;
      if (!requestBody?.content?.[mediaType]?.examples) {
        return;
      }

      const newOperation = structuredClone(operation);
      const newRequestBody = newOperation!.requestBody as OpenAPIV3.RequestBodyObject;
      const examples = newRequestBody.content![mediaType]!.examples!;

      delete examples[exampleName];

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('deleting request body example', { exampleName, mediaType }),
      );
    },

    updateRequestBodyExampleName(newName: string, oldName: string, mediaType: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const requestBody = operation?.requestBody as OpenAPIV3.RequestBodyObject | undefined;
      if (!requestBody?.content?.[mediaType]?.examples) {
        return;
      }

      const newOperation = structuredClone(operation);
      const newRequestBody = newOperation!.requestBody as OpenAPIV3.RequestBodyObject;
      const examples = newRequestBody.content![mediaType]!.examples!;

      if (examples[newName] && newName !== oldName) {
        throw new Error(`An example with the name "${newName}" already exists.`);
      }

      const exampleData = examples[oldName];

      const updatedExample =
        exampleData && typeof exampleData === 'object'
          ? { ...(exampleData as Record<string, unknown>), summary: newName }
          : { summary: newName, value: {} };

      // Rebuild the examples object to preserve order
      const newExamples: Record<string, OpenAPIV3.ExampleObject | OpenAPIV3.ReferenceObject> = {};
      Object.keys(examples).forEach(key => {
        if (key === oldName) {
          newExamples[newName] = updatedExample;
        } else {
          newExamples[key] = examples[key];
        }
      });
      newRequestBody.content![mediaType]!.examples = newExamples;

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('updating request body example name', { newName, oldName, mediaType }),
      );
    },

    updateRequestBodyExampleValue(value: string, exampleName: string, mediaType: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const requestBody = operation?.requestBody as OpenAPIV3.RequestBodyObject | undefined;
      if (!requestBody?.content?.[mediaType]?.examples?.[exampleName]) {
        return;
      }

      const newOperation = structuredClone(operation);
      const newRequestBody = newOperation!.requestBody as OpenAPIV3.RequestBodyObject;
      const examples = newRequestBody.content![mediaType]!.examples!;
      const exampleData = examples[exampleName];

      let parsedValue: unknown = value;
      try {
        parsedValue = JSON.parse(value);
      } catch (e) {
        // Keep as string if parsing fails
      }

      examples[exampleName] =
        exampleData && typeof exampleData === 'object'
          ? { ...(exampleData as Record<string, unknown>), value: parsedValue }
          : { value: parsedValue };

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
        },
        false,
        actionLog('updating request body example value', { value, exampleName, mediaType }),
      );
    },

    getRequestBodyExamples() {
      const { operation } = get().getCurrentOperation();
      const requestBody = operation?.requestBody as OpenAPIV3.RequestBodyObject | undefined;

      const contentTypes = Object.keys(requestBody?.content || {});
      const examples: {
        mediaType: string;
        summary: string;
        value: unknown;
      }[] = [] as {
        mediaType: string;
        summary: string;
        value: unknown;
      }[];

      contentTypes.forEach(contentType => {
        const exampleKeys = Object.keys(requestBody?.content?.[contentType]?.examples || {});
        exampleKeys.forEach(exampleKey => {
          if (
            requestBody?.content?.[contentType]?.examples?.[exampleKey] &&
            '$ref' in requestBody.content[contentType].examples[exampleKey]
          ) {
            return;
          }
          examples.push({
            mediaType: contentType,
            summary: exampleKey,
            value:
              (requestBody?.content?.[contentType]?.examples?.[exampleKey] as OpenAPIV3.ExampleObject)?.value || {},
          });
        });
      });

      return examples.sort((a, b) => {
        if (a.mediaType === MIMEType.ApplicationJSON) return -1;
        if (b.mediaType === MIMEType.ApplicationJSON) return 1;
        return a.mediaType.localeCompare(b.mediaType);
      });
    },
  },
});
